/**
 * Small presentational terminal primitives, extracted verbatim from
 * PromptLanding.jsx so PromptSandbox.jsx can reuse the exact same chrome
 * instead of a second hand-rolled copy.
 */

import { Terminal } from 'lucide-react';
import { TT } from '../terminalTokens';

/* ─── TERM WINDOW — dot-chrome. Per both PromptLanding's and PromptSandbox's
   own layout rules, this should appear on exactly one element per page. ─── */
export const TermWindow = ({ title, children, style = {} }) => (
  <div style={{
    background: TT.bg, borderRadius: TT.radius.xl,
    border: `1px solid ${TT.border}`,
    overflow: 'hidden', fontFamily: TT.fontMono, transition: TT.trans,
    boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
    ...style,
  }}>
    <div style={{
      background: TT.bgSurface, borderBottom: `1px solid ${TT.border}`,
      padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <div style={{ display: 'flex', gap: 7 }}>
        {['#ff5f56', '#ffbd2e', '#27c93f'].map((c) => (
          <div key={c} style={{ width: 12, height: 12, borderRadius: '50%', background: c }} />
        ))}
      </div>
      <span style={{
        flex: 1, textAlign: 'center', fontSize: 11,
        color: TT.textDim, letterSpacing: '0.05em',
      }}>{title}</span>
    </div>
    {children}
  </div>
);

/* ─── TERM HELPERS ─── */
export const TermLine = ({ prompt = '$', children, style = {} }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 13, lineHeight: 1.7, ...style }}>
    <span style={{ color: TT.textDim, fontWeight: 700, userSelect: 'none' }}>{prompt}</span>
    <span style={{ color: TT.text }}>{children}</span>
  </div>
);
export const TermComment = ({ children }) => (
  <div style={{ fontSize: 12, color: TT.textDim, fontStyle: 'italic', lineHeight: 1.6, marginBottom: 4 }}>{'// '}{children}</div>
);
export const TermKeyVal = ({ label, value, valueColor = TT.green }) => (
  <div style={{ display: 'flex', gap: 8, fontSize: 12, lineHeight: 1.8 }}>
    <span style={{ color: TT.textDim, fontSize: 11, minWidth: 100, flexShrink: 0 }}>{label}:</span>
    <span style={{ color: valueColor }}>{value}</span>
  </div>
);


const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/* ─── SKELETON — shared full-page loading chrome for the ai-prompting
   skill: icon tile, pulsing "loading <target>_" line. Extracted from
   PromptLanding.jsx's v11 strip (the glass card +
   shimmer + placeholder bars were removed there; PromptSandbox.jsx had
   its own separate skeleton that never followed that strip, so the two
   pages drifted apart again — this is now the one copy both use). ─── */
export const SkeletonLoader = ({ loadingText = 'ai-prompting.skill' }) => (
  <div style={{
    minHeight: '100vh', backgroundColor: TT.bg,
    backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
    backgroundSize: '22px 22px',
  }}>
    <style>{`
      @keyframes termPulse{0%,100%{opacity:1}50%{opacity:0.3}}
      @keyframes fadeInUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
    `}</style>
    <div className="rail-clearance" style={{
      minHeight: '100vh',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: TT.fontMono,
    }}>
      <div style={{ animation: 'fadeInUp 0.4s ease-out both', textAlign: 'center' }}>
        <div style={{
          width: 64, height: 64, borderRadius: 18,
          background: 'linear-gradient(135deg,rgba(234,88,12,0.2),rgba(234,88,12,0.06))',
          border: '1px solid rgba(234,88,12,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px', boxShadow: '0 0 32px rgba(234,88,12,0.2)',
        }}>
          <Terminal size={32} color={TT.orange} />
        </div>
        <div style={{ fontSize: 11, color: TT.textDim, opacity: 0.6 }}>
          <span style={{ color: TT.orange }}>loading</span> {loadingText}
          <span style={{ animation: prefersReducedMotion() ? 'none' : 'termPulse 1.2s ease-in-out infinite' }}>_</span>
        </div>
      </div>
    </div>
  </div>
);

/* ─── PROGRESS RING ─── */
export const ProgressRing = ({ pct, size = 56, stroke = 5, color = TT.orange }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct / 100);
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={TT.bgSurface} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.22,1,0.36,1)' }} />
      </svg>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ fontSize: size < 60 ? 13 : 12, fontWeight: 900, color: TT.text, lineHeight: 1 }}>{pct}%</span>
      </div>
    </div>
  );
};
